/**
 * Firebase Authentication (#8): optional sign-in so a reader's profile, seen-log,
 * streak, insights, follows them across devices. Reuses the Firebase app already
 * configured for FCM (firebaseConfig). Lazy + best-effort: auth must never block
 * reading, so the SDK is only initialised when sign-in is actually used.
 */
import { getApp, getApps, initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  OAuthProvider,
  deleteUser,
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";
import { firebaseConfig } from "./firebaseConfig";

const ENGAGEMENT_URL =
  (import.meta.env.VITE_ENGAGEMENT_URL as string | undefined)?.replace(/\/+$/, "") ??
  "https://insnip-engagement.insnip-app.workers.dev";

let authInstance: Auth | null = null;
function auth(): Auth {
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  if (!authInstance) authInstance = getAuth(app);
  return authInstance;
}

export type { User };

/** Subscribe to sign-in state. Returns an unsubscribe fn. */
export function onAuthChange(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth(), cb);
}

/** Current Firebase ID token (JWT) for the signed-in user, or null. */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const u = auth().currentUser;
  return u ? u.getIdToken(forceRefresh) : null;
}

export async function signInWithGoogle(): Promise<User | null> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  try {
    const cred = await signInWithPopup(auth(), provider);
    return cred.user;
  } catch (err) {
    const code = (err as { code?: string })?.code ?? "";
    // Popup blocked/closed, in-app browser, COOP, or storage issues → fall back to a
    // full-page redirect, which works where popups don't. Resolves via getRedirectResult.
    if (/popup|cancelled|operation-not-supported|web-storage|missing-initial-state|network-request-failed/i.test(code)) {
      await signInWithRedirect(auth(), provider);
      return null;
    }
    throw err;
  }
}

/** Sign in with Apple. One tap on Face ID devices; falls back to a redirect where popups
 * are blocked (Firebase provider id "apple.com"; needs the Apple provider enabled in the
 * Firebase console). On the native app this is handled by the native plugin instead. */
export async function signInWithApple(): Promise<User | null> {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  try {
    const cred = await signInWithPopup(auth(), provider);
    return cred.user;
  } catch (err) {
    const code = (err as { code?: string })?.code ?? "";
    if (/popup|cancelled|operation-not-supported|web-storage|missing-initial-state|network-request-failed/i.test(code)) {
      await signInWithRedirect(auth(), provider);
      return null;
    }
    throw err;
  }
}

/** Complete a redirect-based sign-in (call once on app start). Triggers onAuthChange. */
export async function completeRedirectSignIn(): Promise<User | null> {
  try {
    const res = await getRedirectResult(auth());
    return res?.user ?? null;
  } catch {
    return null;
  }
}

/** Thrown by the email-code calls with a user-facing message from the Worker. */
export class EmailAuthError extends Error {}

async function postEmailAuth(path: string, payload: Record<string, string>): Promise<Record<string, unknown>> {
  let resp: Response;
  try {
    resp = await fetch(`${ENGAGEMENT_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new EmailAuthError("Network error. Check your connection and try again.");
  }
  let data: Record<string, unknown> = {};
  try {
    data = (await resp.json()) as Record<string, unknown>;
  } catch {
    /* ignore */
  }
  if (!resp.ok) {
    throw new EmailAuthError(typeof data.error === "string" ? data.error : "Something went wrong. Try again.");
  }
  return data;
}

/** Step 1 of email sign-in: the Worker validates the address (syntax + not-disposable + live
 * MX) and emails a 5-digit code. Throws EmailAuthError with a user-facing reason on rejection. */
export async function requestEmailCode(email: string): Promise<void> {
  await postEmailAuth("/auth/email/request", { email: email.trim() });
}

/** Step 2: exchange the 5-digit code for a Firebase custom token and sign in. Returns the
 * signed-in user; throws EmailAuthError (wrong/expired code) otherwise. */
export async function verifyEmailCode(email: string, code: string): Promise<User | null> {
  const data = await postEmailAuth("/auth/email/verify", { email: email.trim(), code: code.trim() });
  const token = typeof data.token === "string" ? data.token : "";
  if (!token) throw new EmailAuthError("Couldn't complete sign-in. Try again.");
  const cred = await signInWithCustomToken(auth(), token);
  return cred.user;
}

export async function signOutUser(): Promise<void> {
  try {
    await signOut(auth());
  } catch {
    /* ignore */
  }
}

/** Permanently delete the signed-in user's account (Apple App Store 5.1.1(v): account deletion
 * must be initiated AND completed inside the app). Returns:
 *  - "deleted": the Firebase account is gone.
 *  - "reauth":  Firebase needs a fresh login before it will delete (session too old); the caller
 *               falls back to the deletion-request page so the user is never stuck.
 *  - "none":    nobody is signed in. */
export async function deleteAccount(): Promise<"deleted" | "reauth" | "none"> {
  const u = auth().currentUser;
  if (!u) return "none";
  try {
    await deleteUser(u);
    return "deleted";
  } catch (err) {
    const code = (err as { code?: string })?.code ?? "";
    if (/requires-recent-login/i.test(code)) return "reauth";
    throw err;
  }
}
